import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  CashBackMembership,
  CashBackToAccountMembership,
  CashBackToCardMembership,
  CashBackToPlatformMembership,
  Membership,
  PointsTransferMembership,
} from '@rd/types/membership.type';
import { getElementBy } from '@rd/utils/getElementBy';
import { Observable } from 'rxjs';
import {
  CreateCashBackMembershipData,
  CreatePointsTransferMembershipData,
  UpdateCashBackMembershipData,
  UpdatePointsTransferMembershipData,
} from '../services/membership-api/membership-api.service';
import {
  ClearCreateAndUpdateCashBackMembershipErrors,
  CreateMembership,
  linkMembership,
  LoadAllMemberships,
  unlinkMembership,
  UpdateMembership,
} from '../store/actions/membership.actions';
import {
  getCashBackMemberships,
  getCashBackMembershipsMap,
  getCashBackToAccountMembership,
  getCashBackToCardMembership,
  getCashBackToPlatformMembership,
  getCreateMembershipsError,
  getCreateMembershipsLoading,
  getCreateMembershipsSuccess,
  getCreateOrUpdateMembershipError,
  getCreateOrUpdateMembershipLoading,
  getLinkOrUnlinkMembershipLoading,
  getMembershipError,
  getMembershipLoading,
  getMemberships,
  getMembershipsMap,
  getPointsTransferMemberships,
  getPreferredCashBackMembership,
  getPreferredPointsTransferMembership,
  getUpdateMembershipsError,
  getUpdateMembershipsLoading,
  getUpdateMembershipsSuccess,
} from '../store/selectors/membership.selector';

@Injectable({
  providedIn: 'root',
})
export class MembershipFacade {
  constructor(private store: Store) {}

  cashBackToAccountMembership$: Observable<CashBackToAccountMembership | null> =
    this.store.select(getCashBackToAccountMembership);

  cashBackToCardMembership$: Observable<CashBackToCardMembership | null> =
    this.store.select(getCashBackToCardMembership);

  cashBackToPlatformMembership$: Observable<CashBackToPlatformMembership | null> =
    this.store.select(getCashBackToPlatformMembership);

  preferredPointsTransferMembership$: Observable<PointsTransferMembership | null> =
    this.store.select(getPreferredPointsTransferMembership);

  preferredCashBackMembership$: Observable<CashBackMembership | null> =
    this.store.select(getPreferredCashBackMembership);

  createOrUpdateMembershipLoading$: Observable<boolean> = this.store.select(
    getCreateOrUpdateMembershipLoading,
  );

  createOrUpdateMembershipError$: Observable<boolean> = this.store.select(
    getCreateOrUpdateMembershipError,
  );

  createMembershipSuccess$: Observable<boolean> = this.store.select(
    getCreateMembershipsSuccess,
  );

  createMembershipError$: Observable<boolean> = this.store.select(
    getCreateMembershipsError,
  );

  createMembershipLoading$: Observable<boolean> = this.store.select(
    getCreateMembershipsLoading,
  );

  updateMembershipError$: Observable<boolean> = this.store.select(
    getUpdateMembershipsError,
  );
  updateMembershipSuccess$: Observable<boolean> = this.store.select(
    getUpdateMembershipsSuccess,
  );
  updateMembershipLoading$: Observable<boolean> = this.store.select(
    getUpdateMembershipsLoading,
  );
  memberships$: Observable<Membership[]> = this.store.select(getMemberships);

  membershipsMap$: Observable<{ [key: string]: Membership }> =
    this.store.select(getMembershipsMap);

  cashBackMembershipsMap$: Observable<{
    [id: string]: CashBackMembership;
  }> = this.store.select(getCashBackMembershipsMap);

  cashBackMemberships$: Observable<CashBackMembership[]> = this.store.select(
    getCashBackMemberships,
  );

  pointsTransferMemberships$: Observable<PointsTransferMembership[]> =
    this.store.select(getPointsTransferMemberships);

  loadMembershipsLoading$: Observable<boolean> =
    this.store.select(getMembershipLoading);

  loadMembershipsError$: Observable<boolean> =
    this.store.select(getMembershipError);

  linkOrUnlinkMembershipLoading$: Observable<boolean> = this.store.select(
    getLinkOrUnlinkMembershipLoading,
  );

  getPointsTransferMembership(
    loyaltyProgramId: string,
  ): Observable<Membership | null> {
    return this.pointsTransferMemberships$.pipe(
      getElementBy(
        (membership) => membership.loyaltyProgramId === loyaltyProgramId,
      ),
    );
  }

  loadMemberships(): void {
    this.store.dispatch(new LoadAllMemberships());
  }

  createMembership(
    membership:
      | CreateCashBackMembershipData
      | CreatePointsTransferMembershipData,
  ): void {
    this.store.dispatch(new CreateMembership(membership));
  }

  updateMembership(
    membership:
      | UpdateCashBackMembershipData
      | UpdatePointsTransferMembershipData,
    isOverlapping: boolean = false,
  ): void {
    const payload: {
      updateMembershipData:
        | UpdateCashBackMembershipData
        | UpdatePointsTransferMembershipData;
      isOverlapping?: true;
    } = {
      updateMembershipData: membership,
    };
    if (isOverlapping) {
      payload.isOverlapping = true;
    }
    this.store.dispatch(new UpdateMembership(payload));
  }

  clearMembershipErrors(): void {
    this.store.dispatch(new ClearCreateAndUpdateCashBackMembershipErrors());
  }

  linkMembership(loyaltyProgramId: string): void {
    this.store.dispatch(linkMembership({ loyaltyProgramId }));
  }

  unlinkMembership(loyaltyProgramId: string): void {
    this.store.dispatch(unlinkMembership({ loyaltyProgramId }));
  }
}
